import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GroupChatMarkdown from "./GroupChatMarkdown";

const formatExpoContentForChat = (content) => {
  const text = String(content || "");
  if (!/<\/?(voice|detail)>/i.test(text)) return text;

  const voice = text.match(/<voice>\s*([\s\S]*?)\s*<\/voice>/i)?.[1]?.trim();
  const detail = text.match(/<detail>\s*([\s\S]*?)\s*<\/detail>/i)?.[1]?.trim();
  if (voice || detail) {
    return [voice, detail].filter(Boolean).join("\n\n");
  }

  return text.replace(/<\/?(voice|detail)>/ig, "").trim();
};

/**
 * 群聊消息流
 * 区分学生/Agent/系统消息，自己的消息与其他人消息颜色不同
 */
const ChatStream = ({ messages, streamingContent, myUserId, onMarkKeyNode, silentTurnNotice }) => {
  const { t } = useTranslation();
  const endRef = useRef(null);
  const [copiedMessageId, setCopiedMessageId] = useState("");

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent, silentTurnNotice]);

  const handleCopyMessage = useCallback((messageId, content) => {
    if (!content) return;
    const markCopied = () => {
      setCopiedMessageId(messageId);
      window.setTimeout(() => setCopiedMessageId(""), 1600);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(content)
        .then(markCopied)
        .catch((error) => console.error("[ChatStream] 复制消息失败:", error));
      return;
    }
    const textarea = document.createElement("textarea");
    textarea.value = content;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      markCopied();
    } catch (error) {
      console.error("[ChatStream] 复制消息失败:", error);
    } finally {
      document.body.removeChild(textarea);
    }
  }, []);

  return (
    <div className="groupchat-messages">
      {messages.map((msg, i) => {
        const isMine = msg.senderType === "student" && myUserId && msg.senderId === myUserId;
        const isAgentLike = msg.senderType === "agent" || msg.senderType === "host";
        const messageDomId = msg.seq != null ? `seq-${msg.seq}-${i}` : `idx-${i}`;
        const displayContent = formatExpoContentForChat(msg.content || "");
        return (
        <div
          key={msg.seq != null ? `seq-${msg.seq}-${i}` : `idx-${i}`}
          className={`groupchat-msg groupchat-msg-${isAgentLike ? "agent" : (msg.senderType || msg.type || "system")} ${isMine ? "groupchat-msg-mine" : ""}`}
          data-groupchat-message-id={messageDomId}
        >
          {msg.senderType === "student" && (
            <div className="msg-student">
              <span className="msg-sender">
                {msg.senderRole ? `${msg.senderName || t("groupchat.student")} (${msg.senderRole})` : (msg.senderName || msg.senderRole || t("groupchat.student"))}
              </span>
              <div className="msg-content">
                <GroupChatMarkdown content={displayContent} />
              </div>
            </div>
          )}
          {isAgentLike && (
            <div className="msg-agent">
              <div className="msg-agent-header">
                <span className="msg-sender msg-agent-name">{msg.senderName}</span>
              </div>
              <div className="msg-content">
                <GroupChatMarkdown content={displayContent} enableTerms />
              </div>
              <div className="msg-agent-actions">
                <button type="button" onClick={() => handleCopyMessage(messageDomId, displayContent)}>
                  {copiedMessageId === messageDomId ? "已复制" : "复制"}
                </button>
                <button
                  type="button"
                  onClick={() => onMarkKeyNode?.({
                    messageDomId,
                    title: String(displayContent || "").replace(/\s+/g, " ").trim().slice(0, 32) || "手动标记节点",
                    meta: msg.senderName || "手动标记",
                  })}
                >
                  标记节点
                </button>
              </div>
            </div>
          )}
          {msg.type === "host_read_receipt" && (
            <div className="msg-host-read-receipt" role="status">
              <span className="host-read-receipt-icon" aria-hidden="true">◉</span>
              <span className="host-read-receipt-label">{msg.content || "已收到发言"}</span>
              <span className="host-read-receipt-detail">{msg.detail || "想让专家回应，可以直接提问或点名某位专家。"}</span>
            </div>
          )}
          {msg.senderType === "system" && (
            <div className="msg-system">
              <GroupChatMarkdown content={displayContent} />
            </div>
          )}
          {msg.type === "phase" && (
            <div className="msg-system msg-phase">
              <GroupChatMarkdown content={msg.content || ""} />
            </div>
          )}
          {msg.type === "training_completed" && (
            <div className="msg-training-completed">
              <span className="training-completed-icon">✓</span>
              <span className="training-completed-text">
                <GroupChatMarkdown content={msg.content || ""} />
              </span>
            </div>
          )}
          {msg.type === "system" && msg.senderType !== "system" && (
            <div className="msg-system">
              <GroupChatMarkdown content={msg.content || ""} />
            </div>
          )}
          {msg.type === "error" && (
            <div className="msg-system msg-error">
              <GroupChatMarkdown content={msg.content || ""} />
            </div>
          )}
        </div>
      );
      })}

      {/* 流式输出中的 Agent */}
      {Object.entries(streamingContent).map(([agentId, data]) => (
        <div key={agentId} className="groupchat-msg groupchat-msg-agent streaming">
          <div className="msg-agent">
            <div className="msg-agent-header">
              <span className="msg-sender msg-agent-name">{data.agentName}</span>
              <span className="msg-agent-subtitle">正在生成回复</span>
            </div>
            <div className="msg-content">
              <GroupChatMarkdown
                content={formatExpoContentForChat(data.content || "")}
                enableTerms
                isStreaming
              />
              <span className="typing-cursor">▌</span>
            </div>
          </div>
        </div>
      ))}

      {silentTurnNotice && (
        <div
          className={`groupchat-silent-turn-notice ${silentTurnNotice.kind || "natural_continue"}`}
          role="status"
          aria-live="polite"
        >
          <span className="groupchat-silent-turn-dot" aria-hidden="true">!</span>
          <span className="groupchat-silent-turn-text">
            <strong>
              {silentTurnNotice.kind === "low_information"
                ? "需要再具体一点"
                : "想让专家回应？"}
            </strong>
            <span>{silentTurnNotice.text}</span>
          </span>
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
};

export default ChatStream;
